import {useCallback} from "react";

import type ConstructionAllDetailsGuiDto from "../dto/construction/ConstructionAllDetailsGuiDto";
import type ApiResponse from "../services/api/types/ApiResponse";
import Api from "../services/auth/types/IsaacApi";
import useApi from "../services/api/hooks/useApi";
import type ConstructionListItem from "../pages/constructionsNew/types/ConstructionListItem";
import IsaacToastWrapperType from "../types/IsaacToastWrapperType";
import ApiHttpVerbs from "../services/api/types/ApiHttpVerbs";
import type EditablePropertyItems from "../types/EditablePropertyItems";
import type {IDefaultConstructionCalculationParameters} from "../types/IDefaultConstructionCalculationParameters";
import type {ModifiedConstructionNewElements} from "../types/ModifiedConstructionNewElements";
import { formatDate } from "../utils/DateFormatting";
import type ConstructionDto from "../dto/constructions/ConstructionDto";
import type ConstructionLocationCodes from "../pages/constructionsNew/types/ConstructionLocationCodes";
import type ConstructionBulkEdit from "../pages/constructionsNew/types/ConstructionBulkEdit";
import type AcceptedMast from "../types/AcceptedMast";
import type MastLocation from "../types/MastLocation";

interface IConstructionNewHook {
  getConstructionById: (id: string) => Promise<ApiResponse<ConstructionAllDetailsGuiDto>>;
  getAllConstructions: () => Promise<ApiResponse<ConstructionListItem[]>>;
  deleteConstructionById: (constructionId: string, correlationGuid: string) => Promise<ApiResponse<void>>;
  getValuesFromMatRef: (constructionElementsName: string, property: string, filter: string) => Promise<ApiResponse<EditablePropertyItems | null>>;
  validateConstructionPropertyChanged: (
    id: string,
    entityName: string,
    propertyName: string,
    entityId?: string,
    propertyValue?: string,
  ) => Promise<string | undefined>;
  getDefaultConstructionCalculationParameters: () => Promise<ApiResponse<IDefaultConstructionCalculationParameters>>;
  validateEditConstructionGeometrically: (
    constructionId: string,
    collection: ModifiedConstructionNewElements,
  ) => Promise<ApiResponse<ModifiedConstructionNewElements>>;
  saveModified: (
      constructionId: string,
      calculationId: string,
      timeStamp: string,
    ) => Promise<ApiResponse<ModifiedConstructionNewElements>>;
  getLocationCodes: () => Promise<ApiResponse<ConstructionLocationCodes[]>>;
  getLocationTypes: () => Promise<ApiResponse<string[]>>;
  bulkEdit: (payload: ConstructionBulkEdit) => Promise<ApiResponse<void>>;
  getAcceptedMast: (id: string) => Promise<ApiResponse<AcceptedMast[]>>;
  getMastLocationsInRange: (mastNumber: string, lambertXCoordinate: number, lambertYCoordinate: number) => Promise<ApiResponse<MastLocation[]>>;
  accept: (
      constructionId: string,
      calculationId: string,
      timeStamp: string,
      acceptedMastIds: string[],
      mastIdsPerMast: Record<string, string[]>,
    ) => Promise<ApiResponse<void>>;
  unaccept: (
      constructionId: string,
      calculationId: string,
      constructionIds: string[],
      mastIdsPerMast: Record<string, string[]>,
    ) => Promise<ApiResponse<void>>;
}

const getICCConstructions = (items: ConstructionDto[]): ConstructionListItem[] => {
  return items.map((construction: ConstructionDto) => ({
    key: construction.constructionId,
    id: construction.constructionId,
    name: construction.name ?? "",
    number: construction.number ?? "",
    phase: construction.phase,
    locationCode: construction.location?.code ?? "",
    locationType: construction.location?.type ?? "",
    isGeometricallyValid: construction.isGeometricallyValid,
    geometricalValidationErrorMessages:
      construction.geometricalValidationErrorMessages,
    createdBy:
      construction.createdBy !== undefined ? construction.createdBy.name : "",
    createdByUpn:
      construction.createdBy !== undefined ? construction.createdBy.upn : "",
    createdOn: formatDate(
      new Date(construction.createdOn),
      "DD/MM/YYYY HH:MM",
    ),
    isSelected: false,
    sortCreatedOn:
      construction.createdOn !== undefined
        ? new Date(construction.createdOn).toISOString()
        : new Date().toISOString(),
    tags: construction.tags,
    status: construction.status,
    lastUpdatedOn: (!construction.lastUpdatedOn ? undefined : formatDate(new Date(construction.lastUpdatedOn),"DD/MM/YYYY HH:MM")),
    updateCalculationTimeStamp: (!construction.updateCalculationTimeStamp ? undefined : formatDate(new Date(construction.updateCalculationTimeStamp),"DD/MM/YYYY HH:MM")),
  }));
};

const useConstructionNewHook = (): IConstructionNewHook => {
  const {fetchData} = useApi();

  const getConstructionById = useCallback(
    async (id: string): Promise<ApiResponse<ConstructionAllDetailsGuiDto>> => {
      const url = `${process.env.REACT_APP_ICC_API_URL}/constructions/${id}/newgui`;
      return await fetchData<ConstructionAllDetailsGuiDto>(url, Api.ICC);
    },
    [fetchData],
  );

  const getAllConstructions = useCallback(async (): Promise<ApiResponse<ConstructionListItem[]>> => {
    const url = `${process.env.REACT_APP_ICC_API_URL}/constructions`;
    const response = await fetchData<ConstructionDto[]>(url, Api.ICC);
    return {
      ...response,
      data:
        response?.data !== null && response?.data !== undefined
          ? getICCConstructions(response.data)
          : null,
    };
  }, [fetchData]);

  const deleteConstructionById = useCallback(
    async (constructionId: string, correlationGuid: string): Promise<ApiResponse<void>> => {
      const url = `${process.env.REACT_APP_ICC_API_URL}/constructions/${constructionId}`;
      return await fetchData<never>(
        url,
        Api.ICC,
        {handlerType: IsaacToastWrapperType.OnlyError},
        ApiHttpVerbs.DELETE,
        {
          "X-Correlation-ID": correlationGuid,
        },
      );
    },
    [fetchData],
  );

  const getValuesFromMatRef = useCallback(
    async (constructionElementsName: string, property: string, filter: string): Promise<ApiResponse<EditablePropertyItems | null>> => {
      const url = `${process.env.REACT_APP_ICC_API_URL}/materialReference/editable/${constructionElementsName}?property=${property}&filter=${filter}`;
      return await fetchData<EditablePropertyItems>(url, Api.ICC);
    },
    [fetchData],
  );

  const validateConstructionPropertyChanged = useCallback(
    async (
      id: string,
      entityName: string,
      propertyName: string,
      entityId?: string,
      propertyValue?: string,
    ): Promise<string | undefined> => {
      const url = `${process.env.REACT_APP_ICC_API_URL}/constructions/${id}/validateeditedconstructionproperty`;
      const response = await fetchData<ApiResponse<never>>(
        url,
        Api.ICC,
        {useGlobalHandler: false},
        ApiHttpVerbs.POST,
        {},
        JSON.stringify({
          EntityName: entityName,
          EntityId: entityId,
          PropertyValue: propertyValue,
          PropertyName: propertyName,
        }),
      );

      return response.success ? undefined : response.errors;
    },
    [fetchData],
  );

  const getDefaultConstructionCalculationParameters = useCallback(
    async (): Promise<ApiResponse<IDefaultConstructionCalculationParameters>> => {
      const url = `${process.env.REACT_APP_ICC_API_URL}/constructions/defaultCalculationParameters`;
      const response = await fetchData<IDefaultConstructionCalculationParameters>(url, Api.ICC, {
        useGlobalHandler: false,
      });

      return response;
    },
    [fetchData],
  );

  const validateEditConstructionGeometrically = useCallback(
    async (
      constructionId: string,
      collection: ModifiedConstructionNewElements,
    ): Promise<ApiResponse<ModifiedConstructionNewElements>> => {
      const url = `${process.env.REACT_APP_ICC_API_URL}/constructions/${constructionId}/validateeditedconstructiongeometrically`;
      const response = await fetchData<ModifiedConstructionNewElements>(
        url,
        Api.ICC,
        {useGlobalHandler: false},
        ApiHttpVerbs.POST,
        {},
        JSON.stringify(collection),
      );
      return response;
    },
    [fetchData],
  );

  const saveModified = useCallback(async (
    constructionId: string,
    calculationId: string,
    timeStamp: string,
  ): Promise<ApiResponse<ModifiedConstructionNewElements>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/constructions/${constructionId}/saveModified`;
    const response = await fetchData<ModifiedConstructionNewElements>(
      url,
      Api.ISAAC,
      {handlerType: IsaacToastWrapperType.Full},
      ApiHttpVerbs.POST,
      {},
      JSON.stringify({calculationId, timeStamp}),
    );
    return response;
  }, [fetchData]);

  const getLocationCodes = useCallback(async (): Promise<ApiResponse<ConstructionLocationCodes[]>> => {
    const url = `${process.env.REACT_APP_ICC_API_URL}/constructions/locationCodes`;

    const response = await fetchData<ConstructionLocationCodes[]>(url, Api.ICC);

    return response;
  }, [fetchData]);

  const getLocationTypes = useCallback(async (): Promise<ApiResponse<string[]>> =>
    await fetchData<string[]>(`${process.env.REACT_APP_ICC_API_URL}/constructions/locationTypes`, Api.ICC), [fetchData]);

  const bulkEdit = useCallback(async (payload: ConstructionBulkEdit): Promise<ApiResponse<void>> => {
    const url = `${process.env.REACT_APP_ICC_API_URL}/constructions/bulkEdit`;

    const response = await fetchData<never>(
      url,
      Api.ICC,
      {handlerType: IsaacToastWrapperType.Full},
      ApiHttpVerbs.POST,
      {},
      JSON.stringify(payload),
    );

    return response;
  }, [fetchData]);

  const getAcceptedMast = useCallback(async (acceptedMastId: string): Promise<ApiResponse<AcceptedMast[]>> => {
    const url = `${process.env.REACT_APP_ICC_API_URL}/acceptedMasts/${acceptedMastId}`;
    const response = await fetchData<AcceptedMast[]>(url, Api.ICC);

    return response;
  }, [fetchData]);

  const getMastLocationsInRange = useCallback(async (
    mastNumber: string,
    lambertXCoordinate: number,
    lambertYCoordinate: number,
  ): Promise<ApiResponse<MastLocation[]>> => {
    const url = `${process.env.REACT_APP_ICC_API_URL}/mastlocationsinrange/${mastNumber}:${lambertXCoordinate}:${lambertYCoordinate}`;
    const response = await fetchData<MastLocation[]>(url, Api.ICC);

    return response;
  }, [fetchData]);

   const accept = useCallback(async (
    constructionId: string,
    calculationId: string,
    timeStamp: string,
    constructionIds: string[],
    mastIdsPerMast: Record<string, string[]>,
  ): Promise<ApiResponse<void>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/constructions/${constructionId}/accept`;
    const response = await fetchData<never>(
      url,
      Api.ISAAC,
      {handlerType: IsaacToastWrapperType.Full},
      ApiHttpVerbs.POST,
      {},
      JSON.stringify({
        timeStamp,
        constructionIds,
        mastIdsPerMast,
        calculationId,
      }),
    );

    return response;
  }, [fetchData]);

  const unaccept = useCallback(async (
    constructionId: string,
    calculationId: string,
    constructionIds: string[],
    mastIdsPerMast: Record<string, string[]>,
  ): Promise<ApiResponse<void>> => {
    const url = `${process.env.REACT_APP_ISAAC_API_URL}/constructions/${constructionId}/unaccept`;

    return await fetchData<never>(
      url,
      Api.ISAAC,
      {handlerType: IsaacToastWrapperType.Full},
      ApiHttpVerbs.POST,
      {},
      JSON.stringify({
        constructionIds,
        mastIdsPerMast,
        calculationId,
      }),
    );
  }, [fetchData]);

  return {
    getConstructionById,
    getAllConstructions,
    deleteConstructionById,
    getValuesFromMatRef,
    validateConstructionPropertyChanged,
    getDefaultConstructionCalculationParameters,
    validateEditConstructionGeometrically,
    saveModified,
    getLocationCodes,
    getLocationTypes,
    bulkEdit,
    getAcceptedMast,
    getMastLocationsInRange,
    accept,
    unaccept,
  };
};

export default useConstructionNewHook;
